import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

const ease = [0.22, 1, 0.36, 1] as const;

interface PremiumWelcomeBannerProps {
  onDismiss: () => void;
  className?: string;
}

export default function PremiumWelcomeBanner({
  onDismiss,
  className = '',
}: PremiumWelcomeBannerProps) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}
      transition={{ duration: 0.5, delay: 0.05, ease }}
      className={`relative overflow-hidden rounded-2xl border border-primary/20 bg-cream-warm p-5 md:p-6 ${className}`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(212,163,115,0.18), transparent 70%)',
        }}
      />

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss welcome message"
        className="absolute top-3 right-3 p-1.5 rounded-full text-text-muted hover:text-text-secondary hover:bg-border/40 transition-colors cursor-pointer"
      >
        <X size={16} />
      </button>

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
        <div className="min-w-0 pr-6 md:pr-0">
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-primary mb-2">
            Premium activated
          </p>
          <h2 className="font-display italic font-light text-2xl md:text-3xl text-text-primary leading-[1.15] mb-1.5">
            Welcome to Premium.
          </h2>
          <p className="font-sans text-sm text-text-secondary leading-relaxed">
            Unlimited mock interviews and full practice mode are now unlocked.
          </p>
        </div>

        <motion.button
          whileTap={{ scale: 0.97 }}
          whileHover={{ y: -1 }}
          onClick={() => navigate('/interview/intro')}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-dark text-white font-semibold font-sans text-sm py-3 px-5 shadow-md shrink-0 cursor-pointer"
        >
          Start unlimited interview
          <ArrowRight size={16} />
        </motion.button>
      </div>
    </motion.div>
  );
}
